import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { format, isValid, parse } from "date-fns";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../Compliance/AuditHub/theme";
import {
  formTableActionBtnSx,
  formTableActionsWrapSx,
  formTableBodyCellSx,
  formTableCardSx,
  formTableDeleteBtnSx,
  formTableHeadCellSx,
} from "./formTableStyles";

/** Same status tones as ProgramsGrid / Audit Hub chips. */
const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const COLUMNS = [
  { key: "number", label: "Task Order" },
  { key: "status", label: "STATUS" },
  { key: "orderDate", label: "ORDER DATE" },
  { key: "start", label: "START" },
  { key: "end", label: "END" },
  { key: "actions", label: "ACTIONS" },
];

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

const statusChipSx = (status) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 600,
    borderRadius: "6px",
    backgroundColor: tone.bg,
    color: tone.fg,
    "& .MuiChip-label": { px: 1 },
  };
};

const TaskOrdersDialog = ({
  open,
  onClose,
  customer: _customer,
  taskOrders,
  loading,
  error,
  onEdit,
  onDelete,
  deletingId,
}) => {
  const [pendingArchive, setPendingArchive] = useState(null);
  const archiveBusy = Boolean(pendingArchive?.id && deletingId === pendingArchive.id);
  const pendingLabel = pendingArchive?.task_order_number || "this workorder";

  useEffect(() => {
    if (!open) setPendingArchive(null);
  }, [open]);

  useEffect(() => {
    if (!pendingArchive?.id) return;
    const stillPresent = (taskOrders || []).some((order) => order.id === pendingArchive.id);
    if (!stillPresent && deletingId == null) {
      setPendingArchive(null);
    }
  }, [taskOrders, pendingArchive, deletingId]);

  const closeArchiveConfirm = () => {
    if (archiveBusy) return;
    setPendingArchive(null);
  };

  const confirmArchive = () => {
    if (!pendingArchive || archiveBusy) return;
    onDelete(pendingArchive);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 1120,
          width: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3, minWidth: 0 }}>
          Task Orders
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2.5 },
          backgroundColor: AH_TRACK,
        }}
      >
        {error ? (
          <Typography sx={{ mb: 1.5, fontSize: 13, color: "#d32f2f" }} role="alert">
            {error}
          </Typography>
        ) : null}

        <TableContainer sx={{ ...formTableCardSx, maxHeight: 520, overflow: "auto" }}>
          <Table size="small" stickyHeader aria-label="Customer workorders" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} sx={formTableHeadCellSx}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ ...formTableBodyCellSx, py: 6, borderBottom: 0 }}
                  >
                    <CircularProgress size={24} sx={{ color: AH_ACCENT }} />
                  </TableCell>
                </TableRow>
              ) : taskOrders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ ...formTableBodyCellSx, py: 6, borderBottom: 0, color: AH_MUTED }}
                  >
                    No workorders for this customer yet.
                  </TableCell>
                </TableRow>
              ) : (
                taskOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell
                      sx={{
                        ...formTableBodyCellSx,
                        fontWeight: 600,
                        color: AH_TEXT,
                        minWidth: 160,
                      }}
                    >
                      {order.task_order_number || "—"}
                    </TableCell>
                    <TableCell sx={formTableBodyCellSx}>
                      <Chip size="small" label={order.status || "—"} sx={statusChipSx(order.status)} />
                    </TableCell>
                    <TableCell sx={formTableBodyCellSx}>{formatDate(order.order_date)}</TableCell>
                    <TableCell sx={formTableBodyCellSx}>{formatDate(order.start_date)}</TableCell>
                    <TableCell sx={formTableBodyCellSx}>{formatDate(order.end_date)}</TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, width: 96, whiteSpace: "nowrap" }}>
                      <Box sx={formTableActionsWrapSx}>
                        <Tooltip title="Edit workorder">
                          <IconButton
                            size="small"
                            aria-label={`Edit ${order.task_order_number || "task order"}`}
                            onClick={() => onEdit(order)}
                            sx={formTableActionBtnSx}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete workorder">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Delete ${order.task_order_number || "task order"}`}
                              disabled={deletingId === order.id || Boolean(pendingArchive)}
                              onClick={() => setPendingArchive(order)}
                              sx={formTableDeleteBtnSx}
                            >
                              {deletingId === order.id ? (
                                <CircularProgress size={14} sx={{ color: "#fca5a5" }} />
                              ) : (
                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>

    <Dialog
      open={Boolean(pendingArchive)}
      onClose={closeArchiveConfirm}
      fullWidth
      maxWidth="sm"
      aria-labelledby="archive-workorder-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 520,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography
          id="archive-workorder-title"
          sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}
        >
          Archive Workorder
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={archiveBusy}
          onClick={closeArchiveConfirm}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: archiveBusy ? "default" : "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Archive{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {pendingLabel}
          </Box>
          ?
        </Typography>
        <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: AH_MUTED, lineHeight: 1.6 }}>
          This removes it from the active workorders list.
        </Typography>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          variant="contained"
          onClick={closeArchiveConfirm}
          disabled={archiveBusy}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#74788d", color: AH_WHITE, opacity: 0.65 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmArchive}
          disabled={archiveBusy}
          startIcon={archiveBusy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ ...primaryButtonSx, height: 36, minHeight: 36, minWidth: 120 }}
        >
          {archiveBusy ? "Archiving…" : "Archive"}
        </Button>
      </Box>
    </Dialog>
    </>
  );
};

export default TaskOrdersDialog;
